import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    TextField,
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import InformationCircleIcon from '@mui/icons-material/Info';
import { NumericFormat, NumberFormatValues } from 'react-number-format';

import { UserAnswers } from '../../types';

import MultiplyIcon from 'modules/common/components/AccountablePlanV2/MultiplyIcon';
import ResponsiveTable from 'modules/common/components/ResponsiveTable';
import { moneyFormat } from 'modules/common/display';
import useMileageRates from 'services/bookkeeping/queries/useMileageRates';
import { selectLatestMileageRate } from 'modules/common/components/AccountablePlanV2/MixedUse/Mileage/utils';
import { MAX_PHONE_INTERNET_PERCENT } from 'modules/common/components/AccountablePlanV2/calculations';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import HomeOfficeTableHead from 'modules/common/components/AccountablePlanV2/MixedUse/HomeOfficeForm/HomeOfficeTableHead';
import PhoneAndInternetTableHead from 'modules/common/components/AccountablePlanV2/MixedUse/PhoneAndInternetForm/PhoneAndInternetTableHead';
import MileageTableHead from 'modules/common/components/AccountablePlanV2/MixedUse/Mileage/MileageTableHead';
import SectionCard from 'modules/common/components/AccountablePlanV2/SectionCard';

interface MixedUseExpenseModalProps {
    open: boolean;
    onClose: () => void;
    onSave?: (amounts: {
        homeOffice: number;
        phoneInternet: number;
        mileage: number;
        cost_security?: number;
        cost_cleaning?: number;
        cost_utilities?: number;
        cost_rent?: number;
        cost_rent_insurance?: number;
        cost_HOA?: number;
        cost_home_insurance?: number;
        cost_property_taxes?: number;
        cost_mortgage_interest?: number;
        cost_internet?: number;
        cost_phone?: number;
        businessMiles?: number;
        phoneInternetBusinessPercent?: number;
    }) => void;
    month: string;
    year: number;
    userAnswers: UserAnswers;
    existingData?: {
        homeOffice: number;
        phoneInternet: number;
        mileage: number;
        cost_security?: number;
        cost_cleaning?: number;
        cost_utilities?: number;
        cost_rent?: number;
        cost_rent_insurance?: number;
        cost_HOA?: number;
        cost_home_insurance?: number;
        cost_property_taxes?: number;
        cost_mortgage_interest?: number;
        cost_internet?: number;
        cost_phone?: number;
        businessMiles?: number;
        phoneInternetBusinessPercent?: number;
    };
}

function MixedUseExpenseModal({
    open,
    onClose,
    onSave = undefined,
    month,
    year,
    userAnswers,
    existingData = {
        homeOffice: 0,
        phoneInternet: 0,
        mileage: 0,
    },
}: MixedUseExpenseModalProps) {
    // Home office expenses - structure based on rent/own status (matching AccountablePlanV2 field names)
    const isRenting = userAnswers.rentOrOwn === 'rent';
    const [homeOfficeExpenses, setHomeOfficeExpenses] = useState({
        // Common expenses for both renters and owners
        cost_security: existingData?.cost_security || 0,
        cost_cleaning: existingData?.cost_cleaning || 0,
        cost_utilities: existingData?.cost_utilities || 0,
        // Renter-specific expenses
        cost_rent: existingData?.cost_rent || 0,
        cost_rent_insurance: existingData?.cost_rent_insurance || 0,
        // Owner-specific expenses
        cost_HOA: existingData?.cost_HOA || 0,
        cost_home_insurance: existingData?.cost_home_insurance || 0,
        cost_mortgage_interest: existingData?.cost_mortgage_interest || 0,
        cost_property_taxes: existingData?.cost_property_taxes || 0,
    });

    // Phone and internet expenses
    const [phoneInternetExpenses, setPhoneInternetExpenses] = useState({
        internet: existingData?.cost_internet || 0,
        phone: existingData?.cost_phone || 0,
    });

    // Mileage expenses
    const [mileageExpenses, setMileageExpenses] = useState({
        businessMiles: existingData?.businessMiles || 0,
    });

    // Multipliers state (only phone/internet editable, home office and mileage are fixed)
    const [multipliers, setMultipliers] = useState({
        phoneInternet: Math.min(existingData?.phoneInternetBusinessPercent || 70, MAX_PHONE_INTERNET_PERCENT),
    });

    // Fixed home office multiplier from user answers
    const homeOfficeMultiplier = parseFloat(userAnswers.percentage);

    // Fetch mileage rate from backend
    const { data: mileageRates } = useMileageRates();
    const mileageRate = selectLatestMileageRate(mileageRates || [], dayjs());

    // Determine which sections to show based on user answers
    const showHomeOffice = userAnswers.usesHomeAsOffice === 'yes';
    const showPhoneInternet = userAnswers.usesHomeAsOffice === 'yes';
    const showMileage = userAnswers.drivesForBusiness === 'yes';

    // Calculate totals
    const homeOfficeTotal = useMemo(() => {
        const total = Object.values(homeOfficeExpenses).reduce((sum, value) => sum + value, 0);
        return total * (homeOfficeMultiplier / 100);
    }, [homeOfficeExpenses, homeOfficeMultiplier]);

    const phoneInternetTotal = useMemo(() => {
        const total = Object.values(phoneInternetExpenses).reduce((sum, value) => sum + value, 0);
        return total * (multipliers.phoneInternet / 100);
    }, [phoneInternetExpenses, multipliers.phoneInternet]);

    const mileageTotal = useMemo(() => {
        return mileageExpenses.businessMiles * parseFloat(mileageRate?.rate || '0');
    }, [mileageExpenses.businessMiles, mileageRate]);

    const handleContinue = () => {
        if (onSave) {
            onSave({
                homeOffice: homeOfficeTotal,
                phoneInternet: phoneInternetTotal,
                mileage: mileageTotal,
                // Individual expense details for proper accountable plan mapping
                cost_security: homeOfficeExpenses.cost_security,
                cost_cleaning: homeOfficeExpenses.cost_cleaning,
                cost_utilities: homeOfficeExpenses.cost_utilities,
                cost_rent: homeOfficeExpenses.cost_rent,
                cost_rent_insurance: homeOfficeExpenses.cost_rent_insurance,
                cost_HOA: homeOfficeExpenses.cost_HOA,
                cost_home_insurance: homeOfficeExpenses.cost_home_insurance,
                cost_property_taxes: homeOfficeExpenses.cost_property_taxes,
                cost_mortgage_interest: homeOfficeExpenses.cost_mortgage_interest,
                cost_internet: phoneInternetExpenses.internet,
                cost_phone: phoneInternetExpenses.phone,
                businessMiles: mileageExpenses.businessMiles,
                phoneInternetBusinessPercent: multipliers.phoneInternet,
            });
        } else {
            onClose();
        }
    };

    const renderHomeOfficeTable = () => (
        <ResponsiveTable>
            <Table>
                <HomeOfficeTableHead />
                <TableBody
                    sx={{
                        '& tr:last-child td': {
                            borderBottom: 0,
                        },
                    }}>
                    {[
                        // Common expenses for both renters and owners
                        { key: 'cost_security', label: 'Alarm & security service' },
                        { key: 'cost_cleaning', label: 'Cleaning' },
                        { key: 'cost_utilities', label: 'Utilities (e.g., gas, water, electricity, etc.)' },
                        // Renter-specific expenses
                        ...(isRenting
                            ? [
                                  { key: 'cost_rent', label: 'Rent' },
                                  { key: 'cost_rent_insurance', label: 'Renters insurance' },
                              ]
                            : []),
                        // Owner-specific expenses
                        ...(!isRenting
                            ? [
                                  { key: 'cost_HOA', label: 'HOA dues' },
                                  { key: 'cost_home_insurance', label: 'Home insurance' },
                                  { key: 'cost_mortgage_interest', label: 'Mortgage interest expense' },
                                  { key: 'cost_property_taxes', label: 'Property taxes' },
                              ]
                            : []),
                    ].map(({ key, label }) => {
                        const value = homeOfficeExpenses[key as keyof typeof homeOfficeExpenses] || 0;
                        const reimbursableAmount = value * (homeOfficeMultiplier / 100);

                        return (
                            <TableRow key={key} sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                                <TableCell width="100%">
                                    <Typography variant="body1">{label}</Typography>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        sx={{ minWidth: '100px' }}
                                        value={value}
                                        inputMode="numeric"
                                        InputProps={{
                                            inputComponent: NumericFormat as React.ComponentType<any>,
                                            inputProps: {
                                                prefix: '$',
                                                style: { textAlign: 'right' },
                                                maxLength: 8,
                                                allowNegative: false,
                                                thousandSeparator: true,
                                                onValueChange: (values: NumberFormatValues) => {
                                                    const parsedValue = values.floatValue || 0;
                                                    setHomeOfficeExpenses((prev) => ({ ...prev, [key]: parsedValue }));
                                                },
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MultiplyIcon />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1">{homeOfficeMultiplier.toFixed(2)}%</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1">=</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1">
                                        {moneyFormat(reimbursableAmount, { minPrecision: 2 })}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </ResponsiveTable>
    );

    const renderPhoneInternetTable = () => (
        <ResponsiveTable>
            <Table>
                <PhoneAndInternetTableHead />
                <TableBody
                    sx={{
                        '& tr:last-child td': {
                            borderBottom: 0,
                        },
                    }}>
                    {[
                        { key: 'internet', label: 'Internet' },
                        { key: 'phone', label: 'Phone' },
                    ].map(({ key, label }) => {
                        const value = phoneInternetExpenses[key as keyof typeof phoneInternetExpenses] || 0;
                        const reimbursableAmount = value * (multipliers.phoneInternet / 100);

                        return (
                            <TableRow key={key} sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                                <TableCell width="100%">
                                    <Typography variant="body1">{label}</Typography>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        sx={{ minWidth: '100px' }}
                                        value={value}
                                        inputMode="numeric"
                                        InputProps={{
                                            inputComponent: NumericFormat as React.ComponentType<any>,
                                            inputProps: {
                                                prefix: '$',
                                                style: { textAlign: 'right' },
                                                maxLength: 8,
                                                allowNegative: false,
                                                thousandSeparator: true,
                                                onValueChange: (values: NumberFormatValues) => {
                                                    const parsedValue = values.floatValue || 0;
                                                    setPhoneInternetExpenses((prev) => ({
                                                        ...prev,
                                                        [key]: parsedValue,
                                                    }));
                                                },
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MultiplyIcon />
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        sx={{ minWidth: '80px' }}
                                        value={multipliers.phoneInternet}
                                        inputMode="numeric"
                                        InputProps={{
                                            inputComponent: NumericFormat as React.ComponentType<any>,
                                            inputProps: {
                                                suffix: '%',
                                                style: { textAlign: 'right' },
                                                maxLength: 5,
                                                allowNegative: false,
                                                decimalScale: 2,
                                                min: 0,
                                                max: MAX_PHONE_INTERNET_PERCENT,
                                                isAllowed: (values: NumberFormatValues) => {
                                                    const inputValue = values.floatValue || 0;
                                                    return inputValue >= 0 && inputValue <= MAX_PHONE_INTERNET_PERCENT;
                                                },
                                                onValueChange: (values: NumberFormatValues) => {
                                                    const newValue = values.floatValue || 0;
                                                    setMultipliers((prev) => ({ ...prev, phoneInternet: newValue }));
                                                },
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1">=</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body1">
                                        {moneyFormat(reimbursableAmount, { minPrecision: 2 })}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </ResponsiveTable>
    );

    const renderMileageTable = () => (
        <ResponsiveTable>
            <Table sx={{ '& thead th': { borderBottom: 0 } }}>
                <MileageTableHead />
                <TableBody
                    sx={{
                        '& tr:last-child td': {
                            borderBottom: 0,
                        },
                    }}>
                    <TableRow sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                        <TableCell width="100%">
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="body1">Business miles driven</Typography>
                                <Tooltip
                                    title={`Mileage multiplier is fixed at $${parseFloat(
                                        mileageRate?.rate || '0'
                                    ).toFixed(2)} per IRS guidelines, and cannot include commuting miles.`}
                                    placement="top">
                                    <InformationCircleIcon sx={{ color: 'grey.500' }} fontSize="small" />
                                </Tooltip>
                            </Stack>
                        </TableCell>
                        <TableCell>
                            <TextField
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: '100px' }}
                                value={mileageExpenses.businessMiles}
                                inputMode="numeric"
                                InputProps={{
                                    inputComponent: NumericFormat as React.ComponentType<any>,
                                    inputProps: {
                                        style: { textAlign: 'right' },
                                        maxLength: 8,
                                        allowNegative: false,
                                        decimalScale: 0,
                                        onValueChange: (values: NumberFormatValues) => {
                                            const parsedValue = values.floatValue || 0;
                                            setMileageExpenses((prev) => ({ ...prev, businessMiles: parsedValue }));
                                        },
                                    },
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <MultiplyIcon />
                        </TableCell>
                        <TableCell align="right">
                            <Typography variant="body1">
                                {moneyFormat(parseFloat(mileageRate?.rate || '0'), { minPrecision: 2 })}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography variant="body1">=</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography variant="body1">{moneyFormat(mileageTotal, { minPrecision: 2 })}</Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </ResponsiveTable>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2.5,
                }}>
                <Typography variant="h3">
                    Mixed-use expenses for {month}, {year}
                </Typography>
                <IconButton onClick={onClose} size="medium" sx={{ backgroundColor: 'cement.100' }}>
                    <CloseXIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }} dividers>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Nothing to report? Leave it blank to submit $0.
                </Typography>

                <Stack gap={3}>
                    {/* Home Office Section */}
                    {showHomeOffice && (
                        <SectionCard title="Home office" reimbursableAmount={homeOfficeTotal}>
                            <Box px={3} py={2}>
                                {renderHomeOfficeTable()}
                            </Box>
                        </SectionCard>
                    )}

                    {/* Phone and Internet Section */}
                    {showPhoneInternet && (
                        <SectionCard title="Phone and internet" reimbursableAmount={phoneInternetTotal}>
                            <Box px={3} py={2}>
                                {renderPhoneInternetTable()}
                            </Box>
                        </SectionCard>
                    )}

                    {/* Mileage Section */}
                    {showMileage && (
                        <SectionCard title="Mileage" reimbursableAmount={mileageTotal}>
                            <Box px={3} py={2}>
                                {renderMileageTable()}
                            </Box>
                        </SectionCard>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleContinue} sx={{ textTransform: 'none', px: 3 }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MixedUseExpenseModal;
